import { memo, useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useRebrandLightSurfaceButtonVariant from 'hooks/useRebrandLightSurfaceButtonVariant'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import { isBedbankOffer, isCruiseOffer, isLEHotel } from 'lib/offer/offerTypes'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import { capitalise } from 'lib/string/stringUtils'
import { normaliseOffersForMap } from 'tripPlanner/utils/mapMarkerTransforms'
import type { TypeaheadTravelDates } from '@luxuryescapes/contract-search'
import type { HotelMapViewData, TripItineraryData } from 'tripPlanner/api/types'
import { findStopByPlaceText, isOfferBookedInItinerary } from 'tripPlanner/utils/chatItinerary'
import ChatHotelCarouselCard from './ChatHotelCarouselCard'
import ChatHotelCarouselFilters from './ChatHotelCarouselFilters'
import ChatWidgetHeading from './ChatWidgetHeading'
import OfferCardLoadingSkeleton from 'components/OfferList/OfferCards/OfferCardLoadingSkeleton'
import LoaderPlayStateContext from 'contexts/LoaderPlayStateContext'
import BodyText from 'LuxKit/Typography/BodyText'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { OfferClickSearchParams } from 'tripPlanner/assistant/contexts/ChatMessageActionsContext'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import useCarouselOccupancyFilter, { isDefaultOccupancy } from './useCarouselOccupancyFilter'
import useCarouselDateFilter, { type CarouselDateFilterState } from './useCarouselDateFilter'
import useCarouselOfferSearch from './useCarouselOfferSearch'
import usePersistCarouselHotelSearch from './usePersistCarouselHotelSearch'
import { resolveCarouselSearchDateState } from './resolveCarouselSearchDates'
import { useCarouselPaginationTracker } from 'tripPlanner/hooks/useCarouselPaginationTracker'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useTripPlannerOfferListTracking from 'tripPlanner/hooks/useTripPlannerOfferListTracking'
import { buildAssistantOfferUrlForOffer } from 'tripPlanner/assistant/utils/assistantOfferUrl'
import { getOfferPropertyName } from 'tripPlanner/assistant/utils/messageEnrichment'
import type { ChatHotelCarouselCardType } from './ChatHotelCarouselFilters.types'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { buildCarouselOccupancyFilterAppliedActivityEntry } from './buildCarouselOccupancyFilterAppliedActivityEntry'
import { buildCarouselDateFilterAppliedActivityEntry } from './buildCarouselDateFilterAppliedActivityEntry'

const TARGET_LOADING_CARDS = 10
const STREAMING_EMPTY_LOADING_CARDS = 3
const COMPACT_HOTEL_CARD_WIDTH = rem(244)
const COMPACT_HOTEL_CARD_MIN_HEIGHT = rem(346)
const COMPACT_HOTEL_CARD_IMAGE_HEIGHT = rem(158)
const COMPACT_EXPERIENCE_CARD_WIDTH = COMPACT_HOTEL_CARD_WIDTH
const COMPACT_CRUISE_CARD_WIDTH = COMPACT_HOTEL_CARD_WIDTH

export type { ChatHotelCarouselCardType as ChatOfferCarouselCardType } from './ChatHotelCarouselFilters.types'

export type CarouselOccupancy = Array<{ adults: number; children?: number; childrenAge?: Array<number> }>

interface Props {
  offerIds: Array<string>
  /** The NLP query - used as title and for map filter label */
  searchQuery?: string
  /** Structured occupancy from the carousel part data */
  occupancy?: CarouselOccupancy
  /** Travel dates from the search (specific, flexible, or anytime) */
  searchDates?: TypeaheadTravelDates
  /** Switches between hotel, experience, and display-only cruise carousel behaviour */
  cardType?: ChatHotelCarouselCardType
  onOfferClick?: (data: { offerId: string; searchParams?: OfferClickSearchParams; offerType?: string }) => void
  onOfferHover?: (offerId: string | undefined) => void
  /** Called when "Map view" button is clicked with normalized offers */
  onMapViewClick?: (data: HotelMapViewData) => void
  /** Current trip itinerary data for "Add to trip" on each card */
  itineraryData?: TripItineraryData
  /** Sends a chat message (used by "Add to trip" on each card) */
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  chatId?: number
  trackingKey: string
  isStreaming?: boolean
  openOfferLinksInNewTab?: boolean
}

const Container = styled.div`
  margin: ${rem(16)} 0;
`

const CardWrapper = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: ${rem(16)};
  padding: ${rem(16)};
  box-shadow: ${props => props.theme.shadow.flat.small};
`

/**
 * The map affordance is drawn as a filled disc, where LuxKit's tertiary kind
 * rests transparent. Hover moves up a step so that state still reads, and the
 * disabled fill is held so the disc does not vanish while offers are fetching.
 *
 * The doubled selector outweighs BaseButton's own `.hash:where(...)` rules
 * instead of tying with them and leaning on injection order.
 */
const MapViewButton = styled(IconButton)`
  && {
    --button-background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    --button-hover-background-color: ${props => props.theme.palette.neutral.surface.lightBackgroundAlt};
    --button-disabled-background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  }
`

const CarouselWrapper = styled.div`
  margin-top: ${rem(16)};
  position: relative;

  [data-testid*="bookmark-button"] {
    display: none !important;
  }

  button[data-direction] {
    z-index: 99;
  }
`

const EmptyStateWrapper = styled.div`
  padding: ${rem(40)} 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};
`

const CompactOfferCardLoadingSkeleton = styled(OfferCardLoadingSkeleton)`
  &.compact-hotel-card,
  &.chat-experience-carousel-card,
  &.chat-cruise-carousel-card {
    width: 100%;
    gap: ${rem(8)};

    > :first-child {
      height: ${COMPACT_HOTEL_CARD_IMAGE_HEIGHT};
      aspect-ratio: auto;
    }

    > :last-child {
      gap: ${rem(8)};
    }

    ${mediaQueryUp.desktop} {
      min-height: ${rem(400)};
      gap: ${rem(16)};

      > :first-child {
        height: auto;
        aspect-ratio: 16 / 9;
      }

      > :last-child {
        gap: ${rem(16)};
      }
    }
  }

  &.compact-hotel-card {
    min-height: ${COMPACT_HOTEL_CARD_MIN_HEIGHT};
  }

  &.chat-experience-carousel-card {
    min-height: ${COMPACT_HOTEL_CARD_MIN_HEIGHT};
  }

  &.chat-cruise-carousel-card {
    min-height: ${COMPACT_HOTEL_CARD_MIN_HEIGHT};
  }
`

function ChatHotelCarousel({
  offerIds,
  searchQuery,
  occupancy: occupancyProp,
  searchDates,
  cardType = 'hotel',
  onOfferClick = noop,
  onOfferHover = noop,
  onMapViewClick = noop,
  itineraryData,
  onSendMessage,
  chatId,
  trackingKey,
  isStreaming = false,
  openOfferLinksInNewTab = false,
}: Props) {
  const intl = useIntl()
  const eventBus = useTripPlannerEventBusOptional()
  const lightSurfaceButtonVariant = useRebrandLightSurfaceButtonVariant()

  const defaultHotelTitleLabel = intl.formatMessage({
    id: 'l06aaJjBUwR-eQ-5874E',
    defaultMessage: 'Top Hotel Picks',
    description: 'Default heading for the hotel carousel when no search query is provided',
  })
  const defaultExperienceTitleLabel = intl.formatMessage({
    id: 'vJhmJAn2elKiRffXAdo5',
    defaultMessage: 'Top Experience Picks',
    description: 'Default heading for the experience carousel when no search query is provided',
  })
  const defaultCruiseTitleLabel = intl.formatMessage({
    id: 's_QqH--qGB4d3h-T7nIa',
    defaultMessage: 'Top Cruise Picks',
    description: 'Default heading for the cruise carousel when no search query is provided',
  })
  const defaultTitleLabel = cardType === 'experience' ?
    defaultExperienceTitleLabel :
    cardType === 'cruise' ?
      defaultCruiseTitleLabel :
      defaultHotelTitleLabel

  // Hand-built English, not defaultTitleLabel above (customer-facing, intl-derived when there's
  // no searchQuery) -- activity displayText is LLM-context only, never customer-facing, and stays
  // English regardless of locale, same rule as every other kind in this family. Same fallback
  // shape (searchQuery, else "Top {type} Picks") but hand-built, matching englishStatusLabel's
  // convention elsewhere.
  const activityCarouselTitle = capitalise(searchQuery || `Top ${cardType === 'experience' ? 'Experience' : cardType === 'cruise' ? 'Cruise' : 'Hotel'} Picks`)
  const mapViewLabel = intl.formatMessage({
    id: 'FAktCqmZIbljAt7U9Qji',
    defaultMessage: 'Map view',
    description: 'Button label to switch to map view of hotel results',
  })
  const applyLabel = intl.formatMessage({
    id: 'WwpiGSJ1HEBTcT0uO6U9',
    defaultMessage: 'Apply',
    description: 'Button label to apply filter selections in hotel carousel dropdowns',
  })
  const occupancyPanelTitle = intl.formatMessage({
    id: 'eJ5ZDiG1837n8yqx4TC_',
    defaultMessage: 'Who is going?',
    description: 'Title for the travellers/guests selection floating panel',
  })
  const datePanelTitle = intl.formatMessage({
    id: 'G-va_y_Ri6pCw2Ek4uPR',
    defaultMessage: 'When',
    description: 'Label for the date search field input in the main search and discovery interface',
  })
  const clearFiltersLabel = intl.formatMessage({
    id: 'OXghqAlhRXvpXTLguQRu',
    defaultMessage: 'Clear filters',
    description: 'Button label to reset all active hotel carousel filters',
  })
  const anytimeLabel = intl.formatMessage({
    id: 'pXZTaVZTYZmkf9DrwAg_',
    defaultMessage: 'Anytime',
    description: 'Date chip label when no specific dates are selected for hotel search',
  })
  const noHotelsMessage = intl.formatMessage({
    id: 'mnXjS_ufg-DZDPW5_gWl',
    defaultMessage: 'No hotels available for your selected filters.',
    description: 'Empty state message when hotel carousel filters return no results',
  })
  const noExperiencesMessage = intl.formatMessage({
    id: 'Z6Kl7F6y2zqdJ4GZWfHD',
    defaultMessage: 'No experiences available for your selected dates.',
    description: 'Empty state message when experience carousel date filters return no results',
  })
  // Distinct from `noHotelsMessage` on purpose. When the svc-trip batch fails to return any
  // offer, the cause is a fetch failure, not inventory and not the user's filters — often on
  // a carousel where the user has set no filters at all. Reusing "No hotels available for
  // your selected filters." there would state something false about the product, which is
  // the very thing hiding the contentless cards was meant to stop.
  const couldNotLoadHotelsMessage = intl.formatMessage({
    id: '-2X_SirBMw5we46rY4p9',
    defaultMessage: 'We couldn\'t load these hotels just now.',
    description: 'Empty state message when the hotel carousel could not fetch any of its offers',
  })

  const initialRooms: Array<App.Occupants> | undefined = useMemo(() => occupancyProp?.map(room => ({
    adults: room.adults,
    children: room.childrenAge?.length ?? room.children ?? 0,
    childrenAge: room.childrenAge,
    infants: 0,
  })), [occupancyProp])

  // Passed into the hook itself (rather than wrapping its returned handleApply) so that Clear
  // filters, which also resets committed rooms, is covered by the same notification without a
  // separate wrapper.
  const handleOccupancyApplied = useCallback((previousRooms: Array<App.Occupants>, newRooms: Array<App.Occupants>) => {
    const entry = buildCarouselOccupancyFilterAppliedActivityEntry(activityCarouselTitle, previousRooms, newRooms)
    if (entry) {
      eventBus?.dispatch({
        type: 'trip_planner_surface_activity',
        entries: [entry],
      })
    }
  }, [activityCarouselTitle, eventBus])

  const occupancy = useCarouselOccupancyFilter(initialRooms, handleOccupancyApplied)
  const { initialDates, focusMonth: calendarFocusMonth } = useMemo(
    () => resolveCarouselSearchDateState({ searchDates, itineraryData, cardType }),
    [cardType, itineraryData, searchDates],
  )

  const handleDateFilterApplied = useCallback((previous: CarouselDateFilterState, next: CarouselDateFilterState) => {
    const entry = buildCarouselDateFilterAppliedActivityEntry(activityCarouselTitle, previous, next)
    if (entry) {
      eventBus?.dispatch({
        type: 'trip_planner_surface_activity',
        entries: [entry],
      })
    }
  }, [activityCarouselTitle, eventBus])

  const dateFilter = useCarouselDateFilter({ anytimeLabel, initialDates, onApplied: handleDateFilterApplied })
  const { clear: clearOccupancy } = occupancy
  const { clear: clearDates } = dateFilter

  const dateFilterActive = dateFilter.dateMode !== DATE_SEARCH_OPTION_IDS.ANYTIME
  const occupancyFilterActive = !isDefaultOccupancy(occupancy.rooms)
  const hasUserEditedFilters = occupancy.hasUserEdited || dateFilter.hasUserEdited
  const filterActive = cardType === 'hotel' ?
    occupancyFilterActive || dateFilterActive :
    cardType === 'experience' && dateFilterActive

  const canShowOccupancyFilter = !!searchQuery && cardType === 'hotel'
  const canShowDateFilter = canShowOccupancyFilter || cardType === 'experience'
  const canFilter = canShowOccupancyFilter || canShowDateFilter

  const search = useCarouselOfferSearch({
    searchQuery,
    cardType,
    rooms: occupancy.rooms,
    filterActive,
    hasUserEditedFilters,
    dateMode: dateFilter.dateMode,
    checkIn: dateFilter.checkIn,
    checkOut: dateFilter.checkOut,
    flexibleMonths: dateFilter.flexibleMonths,
    flexibleNights: dateFilter.flexibleNights,
    originalOfferIds: offerIds,
  })

  const searchStop = itineraryData ? findStopByPlaceText(searchQuery, itineraryData.stops) : undefined
  usePersistCarouselHotelSearch({
    chatId,
    tripStopId: searchStop?.id,
    searchQuery,
    filters: search.hotelSearchFilters,
  })

  const currentSearchParams = useMemo<OfferClickSearchParams | undefined>(() => {
    if (!filterActive) return undefined
    const params: OfferClickSearchParams = cardType === 'hotel' ? { rooms: occupancy.rooms } : {}
    if (dateFilter.dateMode === DATE_SEARCH_OPTION_IDS.SPECIFIC && dateFilter.checkIn && dateFilter.checkOut) {
      params.checkIn = dateFilter.checkIn.format(ISO_DATE_FORMAT)
      params.checkOut = dateFilter.checkOut.format(ISO_DATE_FORMAT)
    } else if (dateFilter.dateMode === DATE_SEARCH_OPTION_IDS.FLEXIBLE) {
      if (dateFilter.flexibleMonths) params.flexibleMonths = dateFilter.flexibleMonths
      if (dateFilter.flexibleNights) params.flexibleNights = dateFilter.flexibleNights
    }
    return params
  }, [cardType, dateFilter.dateMode, dateFilter.checkIn, dateFilter.checkOut, dateFilter.flexibleMonths, dateFilter.flexibleNights, filterActive, occupancy.rooms])

  const selectedDatesAndRoomsFilter = currentSearchParams

  const handleClearFilters = useCallback(() => {
    clearOccupancy()
    clearDates()
  }, [clearOccupancy, clearDates])

  const handleMapViewClick = useCallback(() => {
    const normalisedOffers = normaliseOffersForMap(search.offers)
    const query = searchQuery || defaultTitleLabel
    onMapViewClick({ searchQuery: query, offers: normalisedOffers, searchParams: currentSearchParams })
  }, [onMapViewClick, search.offers, searchQuery, defaultTitleLabel, currentSearchParams])

  const displayTitle = capitalise(searchQuery || defaultTitleLabel)
  const shouldShowTitleLoading = !searchQuery && isStreaming

  const tracking = useTripPlannerOfferListTracking(
    cardType === 'experience' ?
      'experienceChatCarousel' :
      cardType === 'cruise' ?
        'cruiseChatCarousel' :
        'hotelChatCarousel',
    trackingKey,
  )

  const hasMapViewButton = onMapViewClick !== noop && cardType !== 'cruise'
  // An untouched DTO carousel has failed to load when empty; edited searches can
  // legitimately return no matches and should offer to clear their filters.
  const emptyStateFromFetchFailure = (search.isOfferDetailSource && !hasUserEditedFilters) || search.hasSearchError
  const emptyStateMessage = emptyStateFromFetchFailure ?
    couldNotLoadHotelsMessage :
    cardType === 'experience' ? noExperiencesMessage : noHotelsMessage

  // Add/Assign messages must use the property name, not LE flash marketing titles
  // (offer.name). lookup_offer name resolution typeaheads properties only.
  const offerNameMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const offer of search.offers) {
      const name = getOfferPropertyName(offer)
      if (name) {
        map.set(offer.id, name)
      }
    }
    return map
  }, [search.offers])

  const offerTypeMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const offer of search.offers) {
      map.set(offer.id, offer.type)
    }
    return map
  }, [search.offers])

  const offerById = useMemo(() => {
    return new Map(search.offers.map((offer) => [offer.id, offer]))
  }, [search.offers])

  const cruiseOfferById = useMemo(() => {
    const map = new Map<string, App.CruiseOffer>()
    if (cardType !== 'cruise') return map

    for (const offer of search.offers) {
      if (isCruiseOffer(offer)) {
        map.set(offer.id, offer)
      }
    }
    return map
  }, [cardType, search.offers])

  const bookedOfferIds = useMemo(() => {
    const set = new Set<string>()
    for (const id of search.filteredOfferIds) {
      if (isOfferBookedInItinerary(id, itineraryData)) {
        set.add(id)
      }
    }
    return set
  }, [search.filteredOfferIds, itineraryData])

  const offerGeoMap = useMemo(() => {
    const map = new Map<string, { city?: string; coordinates?: { lat: number; lng: number } }>()
    for (const offer of search.offers) {
      let city: string | undefined
      let coordinates: { lat: number; lng: number } | undefined

      if (isLEHotel(offer)) {
        city = offer.locationHeading || undefined
      } else if (isBedbankOffer(offer)) {
        city = offer.property?.location
      }

      const property = (isLEHotel(offer) || isBedbankOffer(offer)) ? offer.property : undefined
      if (property?.latitude != null && property?.longitude != null) {
        coordinates = { lat: property.latitude, lng: property.longitude }
      } else if (offer.type === 'experience') {
        const { location } = offer
        if (location.latitude != null && location.longitude != null) {
          coordinates = { lat: location.latitude, lng: location.longitude }
        }
        city = location.city
      }

      if (city || coordinates) {
        map.set(offer.id, { city, coordinates })
      }
    }
    return map
  }, [search.offers])

  const handleOfferClickWithParams = useCallback((offerId: string) => {
    onOfferClick({ offerId, searchParams: currentSearchParams, offerType: offerTypeMap.get(offerId) })
  }, [onOfferClick, currentSearchParams, offerTypeMap])

  const buildOfferUrl = useCallback((offer: App.AnyOffer | undefined) => {
    if (!openOfferLinksInNewTab || !offer) {
      return undefined
    }

    const result = buildAssistantOfferUrlForOffer({
      offer,
      searchParams: currentSearchParams,
      chatId,
    })

    return result.kind === 'supported' ? result.url : undefined
  }, [chatId, currentSearchParams, openOfferLinksInNewTab])

  const { onCarouselScroll } = useCarouselPaginationTracker(chatId)

  const offerClickHandler = onOfferClick !== noop && !openOfferLinksInNewTab ? handleOfferClickWithParams : undefined
  const offerHoverHandler = onOfferHover !== noop && cardType !== 'cruise' ? onOfferHover : undefined
  const offerCardSize = 'small'
  const isHotelCard = cardType === 'hotel'
  const isExperienceCard = cardType === 'experience'
  const isCruiseCard = cardType === 'cruise'
  const carouselWidth = isHotelCard ? COMPACT_HOTEL_CARD_WIDTH : isExperienceCard ? COMPACT_EXPERIENCE_CARD_WIDTH : isCruiseCard ? COMPACT_CRUISE_CARD_WIDTH : undefined
  const carouselTabletWidth = isHotelCard ? COMPACT_HOTEL_CARD_WIDTH : isExperienceCard ? COMPACT_EXPERIENCE_CARD_WIDTH : isCruiseCard ? COMPACT_CRUISE_CARD_WIDTH : undefined
  const carouselDesktopWidth = isHotelCard || isExperienceCard || isCruiseCard ? 'min-content' : undefined
  const offerCardClassName = isHotelCard ? 'compact-hotel-card' : isExperienceCard ? 'chat-experience-carousel-card' : isCruiseCard ? 'chat-cruise-carousel-card' : undefined
  const loadingCardCount = isStreaming && offerIds.length === 0 ?
    STREAMING_EMPTY_LOADING_CARDS :
    TARGET_LOADING_CARDS

  if (offerIds.length === 0 && !filterActive && !isStreaming) {
    return null
  }

  return (
    <Container>
      <CardWrapper>
        <Group direction="vertical" gap={16}>

          <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between" gap={8}>
            {shouldShowTitleLoading && (
              <TextLoadingBox typography="heading5" width="14ch" />
            )}
            {!shouldShowTitleLoading && (
              <ChatWidgetHeading colour="normal-contrast">
                {displayTitle}
              </ChatWidgetHeading>
            )}
            {hasMapViewButton && (
              <MapViewButton
                variant={lightSurfaceButtonVariant}
                kind="tertiary"
                shape="circle"
                size="medium"
                onClick={handleMapViewClick}
                aria-label={mapViewLabel}
                title={mapViewLabel}
                disabled={search.offersFetching || (isStreaming && offerIds.length === 0)}
              >
                <LineMapIcon />
              </MapViewButton>
            )}
          </Group>
          {canFilter && (
            <ChatHotelCarouselFilters
              cardType={cardType}
              canShowOccupancyFilter={canShowOccupancyFilter}
              canShowDateFilter={canShowDateFilter}
              filterActive={filterActive}
              occupancy={occupancy}
              dateFilter={dateFilter}
              focusMonth={calendarFocusMonth}
              tripStops={itineraryData?.stops}
              applyLabel={applyLabel}
              occupancyPanelTitle={occupancyPanelTitle}
              datePanelTitle={datePanelTitle}
              clearFiltersLabel={clearFiltersLabel}
              onClearFilters={handleClearFilters}
            />
          )}
        </Group>
        <CarouselWrapper>
          {search.showLoadingCards && (
            <LoaderPlayStateContext.Provider value="running">
              <CardCarousel
                snap="start"
                gutterStyle="overflow"
                gap={20}
                pageSize="auto"
                width={carouselWidth}
                tabletWidth={carouselTabletWidth}
                desktopWidth={carouselDesktopWidth}
              >
                {Array.from({ length: loadingCardCount }, (_, i) => (
                  <CompactOfferCardLoadingSkeleton key={`skeleton-${i}`} className={isHotelCard || isExperienceCard || isCruiseCard ? offerCardClassName : undefined} variant="card-default" size={offerCardSize} />
                ))}
              </CardCarousel>
            </LoaderPlayStateContext.Provider>
          )}
          {!search.showLoadingCards && search.showEmptyState && (
            <EmptyStateWrapper>
              <BodyText variant="large" colour="light-foreground-alt" align="center">
                {emptyStateMessage}
              </BodyText>
              {/*
                Only offered when there is actually something to clear. Before this gate the
                button rendered unconditionally, which the svc-trip path made reachable with
                no filters set and — where the carousel has no `searchQuery` — no filter UI
                at all. Worse than useless there: `clear()` sets `hasUserEdited`, which routes
                the carousel into the edited-filter search, and with no query that search can
                never complete, leaving the carousel on skeletons indefinitely.
              */}
              {canFilter && !emptyStateFromFetchFailure && (
                <TextButton variant={lightSurfaceButtonVariant} kind="tertiary" size="small" onClick={handleClearFilters}>
                  {clearFiltersLabel}
                </TextButton>
              )}
            </EmptyStateWrapper>
          )}
          {!search.showLoadingCards && search.filteredOfferIds.length > 0 && (
            <OfferListEventsProvider tracking={tracking}>
              <CardCarousel
                snap="start"
                gutterStyle="overflow"
                gap={20}
                pageSize="auto"
                onScroll={onCarouselScroll}
                width={carouselWidth}
                tabletWidth={carouselTabletWidth}
                desktopWidth={carouselDesktopWidth}
              >
                {search.filteredOfferIds.map((offerId, index) => (
                  // One slot per offer, holding its place from the moment the assistant
                  // names it. On the svc-trip path `filteredOfferIds` carries ids that have
                  // not resolved yet, and each id is its own request, so they land out of
                  // order — a placeholder rendered in the offer's OWN position means a late
                  // arrival fills a gap instead of shoving its neighbours sideways. Keyed by
                  // offerId, so React swaps the placeholder for the card in place.
                  !search.isOfferDetailSource || offerById.has(offerId) ? (
                    <ChatHotelCarouselCard
                      key={offerId}
                      offerId={offerId}
                      index={index}
                      cardType={cardType}
                      offerName={offerNameMap.get(offerId)}
                      offerType={offerTypeMap.get(offerId)}
                      offerCity={offerGeoMap.get(offerId)?.city}
                      offerCoordinates={offerGeoMap.get(offerId)?.coordinates}
                      itineraryData={itineraryData}
                      isBooked={bookedOfferIds.has(offerId)}
                      onSendMessage={onSendMessage}
                      onOfferClick={offerClickHandler}
                      onOfferHover={offerHoverHandler}
                      offerUrl={buildOfferUrl(offerById.get(offerId))}
                      selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
                      filterNights={dateFilter.filterNights}
                      openDatePicker={dateFilter.openDatePicker}
                      offer={offerById.get(offerId)}
                      cruiseOffer={cruiseOfferById.get(offerId)}
                      size={offerCardSize}
                      isStreaming={isStreaming}
                      className={offerCardClassName}
                      isOfferDetailSource={search.isOfferDetailSource}
                      offerDetail={search.offerDetails.find(result => result.offerId === offerId && result.status === 'ok')}
                      // Always undefined for an id that reached this branch — the hook has
                      // already dropped failed ids. Passed anyway so the card's own guard
                      // stays live for a caller that stops filtering.
                      unavailableReason={search.unavailableReasonsByOfferId.get(offerId)}
                    />
                  ) : (
                    <CompactOfferCardLoadingSkeleton
                      key={offerId}
                      className={offerCardClassName}
                      variant="card-default"
                      size={offerCardSize}
                    />
                  )
                ))}
              </CardCarousel>
            </OfferListEventsProvider>
          )}
        </CarouselWrapper>
      </CardWrapper>
    </Container>
  )
}

export default memo(ChatHotelCarousel)
